import React, { useState, useCallback, useEffect } from "react";
import { useParams, useNavigate, useSearchParams } from "react-router-dom";
import {
  Box,
  Card,
  CardBody,
  VStack,
  HStack,
  Text,
  Image,
  Button,
  Badge,
  Divider,
  SimpleGrid,
  Heading,
  Spinner,
  Center,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalBody,
  ModalCloseButton,
  IconButton,
  Flex,
  useToast,
} from "@chakra-ui/react";
import {
  FiArrowLeft,
  FiEdit,
  FiTrash2,
  FiZoomIn,
  FiMaximize2,
  FiPackage,
  FiDollarSign,
  FiInfo,
  FiSettings,
} from "react-icons/fi";
import { toast } from "react-toastify";
import { baseUrl } from "../../utils/utils";

// Image Viewer Component
const ImageViewer = ({ src, alt, isOpen, onClose }) => {
  const [imageLoaded, setImageLoaded] = useState(false);
  const [imageError, setImageError] = useState(false);

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="6xl" isCentered>
      <ModalOverlay bg="blackAlpha.800" />
      <ModalContent bg="transparent" shadow="none" maxW="90vw" maxH="90vh">
        <ModalCloseButton
          color="white"
          bg="blackAlpha.600"
          _hover={{ bg: "blackAlpha.800" }}
          size="lg"
          top={4}
          right={4}
          zIndex={2}
        />
        <ModalBody
          p={0}
          display="flex"
          alignItems="center"
          justifyContent="center"
        >
          <Box position="relative" maxW="100%" maxH="100%">
            {!imageLoaded && !imageError && (
              <Center
                position="absolute"
                top="50%"
                left="50%"
                transform="translate(-50%, -50%)"
              >
                <Spinner size="xl" color="white" />
              </Center>
            )}
            {imageError ? (
              <Center
                bg="gray.100"
                borderRadius="md"
                p={20}
                minH="400px"
                minW="400px"
              >
                <VStack>
                  <FiPackage size={48} color="gray.400" />
                  <Text color="gray.500">Image not available</Text>
                </VStack>
              </Center>
            ) : (
              <Image
                src={src}
                alt={alt}
                maxW="100%"
                maxH="90vh"
                objectFit="contain"
                borderRadius="md"
                onLoad={() => setImageLoaded(true)}
                onError={() => setImageError(true)}
              />
            )}
          </Box>
        </ModalBody>
      </ModalContent>
    </Modal>
  );
};

// Product Detail Page Component
const ProductDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();

  // Get return page info from URL params
  const returnPage = searchParams.get('returnPage') || '1';
  const returnRows = searchParams.get('returnRows') || '5';

  const [productData, setProductData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Navigate back to products with preserved pagination
  const navigateBackToProducts = () => {
    navigate(`/products?page=${returnPage}&rows=${returnRows}`);
  };

  // Fetch product data
  const fetchProductData = useCallback(async () => {
    if (!id) return;

    try {
      setIsLoading(true);
      const formData = new FormData();
      formData.append("action", "product_by_id");
      formData.append("id", id);

      const response = await fetch(baseUrl + "product.php", {
        method: "POST",
        body: formData,
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch product: ${response.statusText}`);
      }

      const result = await response.json();

      if (result.status === "error") {
        throw new Error(result.message || "Failed to fetch product data");
      }

      setProductData(result.reponselist || result.data || result);
    } catch (error) {
      console.error("Error fetching product:", error);
      toast({
        title: "Error",
        description: "Failed to load product data",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
      navigateBackToProducts();
    } finally {
      setIsLoading(false);
    }
  }, [id, toast]);

  // Delete product
  const handleDelete = async () => {
    if (!productData) return;

    try {
      setIsDeleting(true);
      const formData = new FormData();
      formData.append("action", "delete");
      formData.append("id", productData.id);

      const response = await fetch(baseUrl + "product.php", {
        method: "POST",
        body: formData,
      });

      const result = await response.json();

      if (response.ok && result.status !== "error") {
        toast({
          title: "Success",
          description: "Product deleted successfully",
          status: "success",
          duration: 3000,
          isClosable: true,
        });
        navigateBackToProducts();
      } else {
        throw new Error(result.message || "Failed to delete product");
      }
    } catch (error) {
      console.error("Delete error:", error);
      toast({
        title: "Error",
        description: error.message || "Failed to delete product",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsDeleting(false);
    }
  };

  // Navigate to edit with return context
  const handleEdit = () => {
    navigate(`/products/edit/${productData.id}?returnPage=${returnPage}&returnRows=${returnRows}`);
  };

  // Fetch data on component mount
  useEffect(() => {
    fetchProductData();
  }, [fetchProductData]);

  // Format currency
  const formatCurrency = (amount) => {
    if (!amount) return "Not specified";
    return `₹ ${parseFloat(amount).toLocaleString("en-IN")}`;
  };

  const calculateTaxableAmount = () => {
    const rsp = parseFloat(productData?.rsp || productData?.price || 0);
    const discount = parseFloat(productData?.discount || 0);
    if (discount > 100) return 0; // to be safe
    return rsp * (1 - discount / 100);
  };

  // Get status color
  const getStatusColor = (status) => {
    if (!status) return "gray";
    switch (status.toLowerCase()) {
      case "active":
      case "available":
      case "in stock":
        return "green";
      case "inactive":
      case "unavailable":
      case "out of stock":
        return "red";
      case "pending":
        return "orange";
      default:
        return "blue";
    }
  };

  if (isLoading) {
    return (
      <Center minH="60vh">
        <VStack spacing={4}>
          <Spinner size="xl" color="blue.500" />
          <Text color="gray.600">Loading product details...</Text>
        </VStack>
      </Center>
    );
  }

  if (!productData) {
    return (
      <Center minH="60vh">
        <VStack spacing={4}>
          <FiPackage size={48} color="gray.400" />
          <Text color="gray.500">Product not found</Text>
          <Button
            onClick={navigateBackToProducts}
            leftIcon={<FiArrowLeft />}
          >
            Back to Products
          </Button>
        </VStack>
      </Center>
    );
  }

  return (
    <Box>
      <VStack align="stretch">
        {/* Header */}
        <Card shadow="md">
          <CardBody>
            <Flex justify="space-between" align="center" wrap="wrap" gap={4}>
              <HStack spacing={4}>
                <Button
                  leftIcon={<FiArrowLeft />}
                  variant="outline"
                  onClick={navigateBackToProducts}
                  size="sm"
                >
                  Back to Products
                </Button>
                <Divider orientation="vertical" h="30px" />
                <VStack align="flex-start" spacing={1}>
                  <Text fontSize="2xl" fontWeight="bold" color="gray.800">
                    {productData.product_name ||
                      productData.name ||
                      "Product Details"}
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    Product ID: {productData.id}
                  </Text>
                </VStack>
              </HStack>

              <HStack spacing={2}>
                <Button
                  leftIcon={<FiEdit />}
                  colorScheme="blue"
                  variant="outline"
                  size="sm"
                  onClick={handleEdit}
                >
                  Edit Product
                </Button>
                <Button
                  leftIcon={<FiTrash2 />}
                  colorScheme="red"
                  variant="outline"
                  size="sm"
                  isLoading={isDeleting}
                  loadingText="Deleting..."
                  onClick={handleDelete}
                >
                  Delete
                </Button>
              </HStack>
            </Flex>
          </CardBody>
        </Card>

        <SimpleGrid columns={[1, 1, 2]} spacing={6}>
          {/* Product Image */}
          <Card shadow="lg">
            <CardBody>
              <VStack spacing={4}>
                <Text fontSize="lg" fontWeight="semibold" color="gray.700">
                  Product Image
                </Text>
                <Box position="relative" w="full">
                  {productData.url ? (
                    <Box position="relative">
                      <Image
                        src={productData.url}
                        alt={productData.description || "Product"}
                        w="full"
                        h="300px"
                        objectFit="contain"
                        borderRadius="md"
                        fallback={
                          <Center
                            bg="gray.100"
                            h="300px"
                            borderRadius="md"
                            border="2px dashed"
                            borderColor="gray.300"
                          >
                            <VStack>
                              <FiPackage size={48} color="gray.400" />
                              <Text color="gray.500">No image available</Text>
                            </VStack>
                          </Center>
                        }
                      />
                      <IconButton
                        icon={<FiMaximize2 />}
                        position="absolute"
                        top={2}
                        right={2}
                        size="sm"
                        colorScheme="whiteAlpha"
                        bg="blackAlpha.600"
                        color="white"
                        _hover={{ bg: "blackAlpha.800" }}
                        onClick={onOpen}
                        aria-label="View full image"
                      />
                    </Box>
                  ) : (
                    <Center
                      bg="gray.100"
                      h="300px"
                      borderRadius="md"
                      border="2px dashed"
                      borderColor="gray.300"
                    >
                      <VStack>
                        <FiPackage size={48} color="gray.400" />
                        <Text color="gray.500">No image uploaded</Text>
                      </VStack>
                    </Center>
                  )}
                </Box>
              </VStack>
            </CardBody>
          </Card>

          {/* Basic Information */}
          <Card shadow="lg">
            <CardBody>
              <VStack spacing={4} align="stretch">
                <HStack justify="space-between" align="center">
                  <Text fontSize="lg" fontWeight="semibold" color="gray.700">
                    Basic Information
                  </Text>
                  <Badge
                    colorScheme={getStatusColor(productData.status)}
                    px={3}
                    py={1}
                    borderRadius="full"
                  >
                    {productData.status || "Active"}
                  </Badge>
                </HStack>

                <VStack spacing={3} align="stretch">
                  <HStack justify="space-between">
                    <Text fontWeight="medium" color="gray.600">
                      Product Name:
                    </Text>
                    <Text color="gray.800">
                      {productData.description || "Not specified"}
                    </Text>
                  </HStack>
                  <Divider />
                  <HStack justify="space-between">
                    <Text fontWeight="medium" color="gray.600">
                      Category:
                    </Text>
                    <Text color="gray.800">
                      {productData.category || "Not specified"}
                    </Text>
                  </HStack>

                  <Divider />

                  <HStack justify="space-between">
                    <Text fontWeight="medium" color="gray.600">
                      Material:
                    </Text>
                    <Text color="gray.800">
                      {productData.mat ||
                        productData.material ||
                        "Not specified"}
                    </Text>
                  </HStack>

                  <Divider />

                  <HStack justify="space-between">
                    <Text fontWeight="medium" color="gray.600">
                      Units:
                    </Text>
                    <Text color="gray.800">
                      {productData.uom || productData.units || "Not specified"}
                    </Text>
                  </HStack>

                  <Divider />

                  <HStack justify="space-between" align="flex-start">
                    <Text fontWeight="medium" color="gray.600">
                      HSN Code:
                    </Text>
                    <Text color="gray.800" textAlign="right">
                      {productData.hsn_no || "Not specified"}
                    </Text>
                  </HStack>

                  <Divider />

                  <HStack justify="space-between">
                    <Text fontWeight="medium" color="gray.600">
                      GST %:
                    </Text>
                    <Text color="gray.800">
                      {productData.gst
                        ? `${productData.gst}%`
                        : "Not specified"}
                    </Text>
                  </HStack>

                  <Divider />

                  <HStack justify="space-between">
                    <Text fontWeight="medium" color="gray.600">
                      Type:
                    </Text>
                    <Text color="gray.800">
                      {productData.type || "Not specified"}
                    </Text>
                  </HStack>
                </VStack>
              </VStack>
            </CardBody>
          </Card>
        </SimpleGrid>

        {/* Pricing & Measurements */}
        <SimpleGrid columns={[1, 2]} spacing={6}>
          {/* Pricing Information */}
          <Card shadow="lg">
            <CardBody>
              <VStack spacing={4} align="stretch">
                <HStack>
                  <FiDollarSign color="green.500" />
                  <Text fontSize="lg" fontWeight="semibold" color="gray.700">
                    Pricing Information
                  </Text>
                </HStack>

                <SimpleGrid columns={2} spacing={4}>
                  <VStack align="flex-start" spacing={1}>
                    <Text fontSize="sm" color="gray.500">
                      RSP
                    </Text>
                    <Text fontSize="xl" fontWeight="bold" color="green.600">
                      {formatCurrency(productData.rsp || productData.price)}
                    </Text>
                  </VStack>

                  <VStack align="flex-start" spacing={1}>
    <Text fontSize="sm" color="gray.500">
      Fixed Rate
    </Text>
    <Text fontSize="xl" fontWeight="bold" color="teal.600">
      {formatCurrency(productData.fixed_rate)}
    </Text>
  </VStack>

                  <VStack align="flex-start" spacing={1}>
                    <Text fontSize="sm" color="gray.500">
                      Discount (%)
                    </Text>
                    <Text fontSize="xl" fontWeight="bold" color="blue.600">
                      {productData.discount ? `${productData.discount}%` : "0%"}
                    </Text>
                  </VStack>

                  <VStack align="flex-start" spacing={1}>
                    <Text fontSize="sm" color="gray.500">
                    Unit Rate
                    </Text>
                    <Text fontSize="xl" fontWeight="bold" color="purple.600">
                      {productData.unit_rate ? productData.unit_rate :"0.00" }
                    </Text>
                  </VStack>

                  <VStack align="flex-start" spacing={1}> 
                    <Text fontSize="sm" color="gray.500">
                      Security Deposit
                    </Text>
                    <Text
                      fontSize="lg"
                      fontWeight="semibold"
                      color="orange.600"
                    >
                      {formatCurrency(productData.securitydeposit)}
                    </Text>
                  </VStack>
                </SimpleGrid>
              </VStack>
            </CardBody>
          </Card>

          {/* Measurements */}
          <Card shadow="lg">
            <CardBody>
              <VStack spacing={4} align="stretch">
                <HStack>
                  <FiSettings color="blue.500" />
                  <Text fontSize="lg" fontWeight="semibold" color="gray.700">
                    Measurements
                  </Text>
                </HStack>

                <SimpleGrid columns={2} spacing={4}>
                  <VStack align="flex-start" spacing={1}>
                    <Text fontSize="sm" color="gray.500">
                      Capacity
                    </Text>
                    <Text fontWeight="medium">
                      {productData.capacity || "Not specified"}{" "}
                      {productData.uom || ""}
                    </Text>
                  </VStack>

                  <VStack align="flex-start" spacing={1}>
                    <Text fontSize="sm" color="gray.500">
                      Weight
                    </Text>
                    <Text fontWeight="medium">
                      {productData.wt || productData.weight || "Not specified"}{" "}
                      kg
                    </Text>
                  </VStack>

                  <VStack align="flex-start" spacing={1}>
                    <Text fontSize="sm" color="gray.500">
                      Height
                    </Text>
                    <Text fontWeight="medium">
                      {productData.ht || productData.height || "Not specified"}{" "}
                      mm
                    </Text>
                  </VStack>

                  <VStack align="flex-start" spacing={1}>
                    <Text fontSize="sm" color="gray.500">
                      Diameter
                    </Text>
                    <Text fontWeight="medium">
                      {productData.dia ||
                        productData.diameter ||
                        "Not specified"}{" "}
                      mm
                    </Text>
                  </VStack>

                  <VStack align="flex-start" spacing={1}>
                    <Text fontSize="sm" color="gray.500">
                      Thickness
                    </Text>
                    <Text fontWeight="medium">
                      {productData.thick ||
                        productData.thickness ||
                        "Not specified"}{" "}
                      mm
                    </Text>
                  </VStack>

                  <VStack align="flex-start" spacing={1}>
                    <Text fontSize="sm" color="gray.500">
                      Water Capacity
                    </Text>
                    <Text fontWeight="medium">
                      {productData.wc ||
                        productData.waterCapacity ||
                        "Not specified"}{" "}
                      ltr
                    </Text>
                  </VStack>
                </SimpleGrid>
              </VStack>
            </CardBody>
          </Card>
        </SimpleGrid>

        {/* Descriptions */}
        <SimpleGrid columns={[1, 1, 2]} spacing={6}>
          {/* Description */}
          <Card shadow="lg">
            <CardBody>
              <VStack spacing={4} align="stretch">
                <HStack>
                  <FiInfo color="purple.500" />
                  <Text fontSize="lg" fontWeight="semibold" color="gray.700">
                    Description
                  </Text>
                </HStack>
                <Text color="gray.600" lineHeight="tall">
                  {productData.description || "No description available"}
                </Text>
              </VStack>
            </CardBody>
          </Card>

          {/* Specifications */}
          <Card shadow="lg">
            <CardBody>
              <VStack spacing={4} align="stretch">
                <Text fontSize="lg" fontWeight="semibold" color="gray.700">
                  Specifications
                </Text>
                <Text color="gray.600" lineHeight="tall">
                  {productData.specifications || "No specifications available"}
                </Text>
              </VStack>
            </CardBody>
          </Card>
        </SimpleGrid>

        {/* Application */}
        <Card shadow="lg">
          <CardBody>
            <VStack spacing={4} align="stretch">
              <Text fontSize="lg" fontWeight="semibold" color="gray.700">
                Application
              </Text>
              <Text color="gray.600" lineHeight="tall">
                {productData.application ||
                  "No application information available"}
              </Text>
            </VStack>
          </CardBody>
        </Card>
      </VStack>

      {/* Image Viewer Modal */}
      <ImageViewer
        src={productData?.url || productData?.image_url}
        alt={productData?.product_name || "Product"}
        isOpen={isOpen}
        onClose={onClose}
      />
    </Box>
  );
};

export default ProductDetailPage;